import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Star,
  Calendar,
  Clock,
  Plane,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  X,
} from 'lucide-react'
import { usePackages, PackageFilters } from '../hooks/usePackages'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { usePackageTypes } from '../hooks/usePackageTypes'
import { useDestinations } from '../hooks/useDestinations'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'

const categories = ['All', 'Luxury', 'Golf', 'Beach', 'City Break', 'Adults Only', 'Eco-Friendly']

export default function Packages() {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Advanced filter states
  const [selectedDestination, setSelectedDestination] = useState('')
  const [selectedPackageType, setSelectedPackageType] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [priceRange, setPriceRange] = useState('')

  // Read initial page from URL
  const queryParams = new URLSearchParams(window.location.search)
  const initialPage = parseInt(queryParams.get('page') || '1', 10)

  // Function to update URL with new page
  const updateUrlPage = (page: number) => {
    window.history.pushState({}, '', `/packages?page=${page}`)
  }

  const { franchiseData, loading: franchiseLoading } = useFranchiseData()
  const { packageTypes, loading: packageTypesLoading } = usePackageTypes()
  const { destinations, loading: destinationsLoading } = useDestinations()
  const {
    packages,
    pagination,
    loading: packagesLoading,
    error,
    applyFilters,
    goToNextPage,
    goToPrevPage,
    loadPage,
  } = usePackages({ initialPage }) // Pass initial page properly

  const loading = franchiseLoading || packagesLoading

  // Override pagination functions to update URL
  const goToNextPageWithUrl = () => {
    if (pagination.has_next) {
      const nextPage = pagination.current_page + 1
      loadPage(nextPage)
      updateUrlPage(nextPage)
    }
  }

  const goToPrevPageWithUrl = () => {
    if (pagination.has_prev) {
      const prevPage = pagination.current_page - 1
      loadPage(prevPage)
      updateUrlPage(prevPage)
    }
  }

  const loadPageWithUrl = (page: number) => {
    loadPage(page)
    updateUrlPage(page)
  }

  const buildFilters = (searchOverride?: string): PackageFilters => {
    const filters: PackageFilters = {}

    if (selectedCategory !== 'All') {
      filters.category = selectedCategory
    }

    const search = searchOverride ?? searchTerm
    if (search) {
      filters.query = search
    }

    if (selectedDestination) {
      filters.destination = parseInt(selectedDestination)
    }

    if (selectedPackageType) {
      filters.offer_type = parseInt(selectedPackageType)
    }

    if (dateFrom) {
      filters.date_from = dateFrom
    }

    if (dateTo) {
      filters.date_to = dateTo
    }

    if (priceRange) {
      filters.price = priceRange
    }

    return filters
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    applyFilters(buildFilters())
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters(buildFilters())
  }

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const hadTerm = searchTerm.trim().length > 0
    setSearchTerm(value)

    // Emptying the box (backspace, cut, select-all delete) clears the previous
    // search immediately and restores the listing - other filters stay active.
    // Typing a non-empty term still waits for submit.
    if (!value.trim() && hadTerm) {
      applyFilters(buildFilters(''))
    }
  }

  const handleAdvancedFilterApply = () => {
    applyFilters(buildFilters())
    setShowAdvancedFilters(false)
  }

  const clearAllFilters = () => {
    setSelectedCategory('All')
    setSearchTerm('')
    setSelectedDestination('')
    setSelectedPackageType('')
    setDateFrom('')
    setDateTo('')
    setPriceRange('')
    applyFilters({})
  }

  const hasActiveFilters =
    selectedCategory !== 'All' ||
    searchTerm ||
    selectedDestination ||
    selectedPackageType ||
    dateFrom ||
    dateTo ||
    priceRange

  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) {
      return pkg.cover_photo.large_url
    }
    if (pkg.cover_photo?.url) {
      return pkg.cover_photo.url
    }
    if (pkg.scraped_cover_photo) {
      return pkg.scraped_cover_photo
    }
    return null
  }

  if (loading && !packages.length) {
    return (
      <div className="bg-indojet-light py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="mx-auto max-w-2xl text-center">
              <div className="h-10 bg-gray-300 rounded w-64 mx-auto mb-6"></div>
              <div className="h-6 bg-gray-300 rounded w-96 mx-auto"></div>
            </div>
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex flex-col items-start">
                  <div className="aspect-[16/9] w-full rounded-2xl bg-gray-300"></div>
                  <div className="mt-8 space-y-3 w-full">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-6 bg-gray-300 rounded w-full"></div>
                    <div className="h-16 bg-gray-300 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-indojet-light py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Something went wrong
            </h2>
            <p className="mt-6 text-lg leading-8 text-red-600">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-8 btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-indojet-light py-24 sm:py-32">
      <Helmet>
        <title>Holiday Packages | Chelsea Travel</title>
        <meta
          name="description"
          content="Discover curated travel packages to top destinations. Filter by type, dates, destination, and price to find your perfect trip."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/packages' : '/packages'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Holiday Packages | Chelsea Travel" />
        <meta
          property="og:description"
          content="Discover curated travel packages to top destinations."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/packages'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Holiday Packages',
            url: typeof window !== 'undefined' ? window.location.href : '/packages',
            description:
              'Discover curated travel packages to top destinations. Filter by type, dates, destination, and price to find your perfect trip.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Packages',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/packages',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Holiday Packages
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Discover our exclusive collection of carefully curated travel packages to amazing
            destinations around the world.
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="mx-auto mt-12 max-w-4xl">
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-md transition-colors ${
                  showAdvancedFilters
                    ? 'border-[#182c78] bg-[#182c78] text-white'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>
              <button type="submit" className="btn-primary">
                Search
              </button>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Destination Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Destination
                    </label>
                    <select
                      value={selectedDestination}
                      onChange={(e) => setSelectedDestination(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">All Destinations</option>
                      {destinations.map((destination) => (
                        <option key={destination.id} value={destination.id}>
                          {destination.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Package Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Package Type
                    </label>
                    <select
                      value={selectedPackageType}
                      onChange={(e) => setSelectedPackageType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">All Types</option>
                      {packageTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Date To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Price Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price Range
                    </label>
                    <select
                      value={priceRange}
                      onChange={(e) => setPriceRange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Any Price</option>
                      <option value="0">Under R5000</option>
                      <option value="5-10">R5000 - R10000</option>
                      <option value="10-20">R10000 - R20000</option>
                      <option value="20">R20000 Upwards</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Clear All Filters
                  </button>
                  <button type="button" onClick={handleAdvancedFilterApply} className="btn-primary">
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Categories Filter */}
        {/* <div className="mx-auto mt-8 flex flex-wrap justify-center gap-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-[#182c78] text-white'
                  : 'bg-gray-100 text-gray-900 hover:bg-[#182c78] hover:text-white'
              }`}
            >
              {category}
            </button>
          ))}
        </div> */}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mx-auto mt-6 max-w-4xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedCategory !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#182c78] text-white text-sm">
                  Category: {selectedCategory}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSelectedCategory('All')}
                  />
                </span>
              )}
              {selectedDestination && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                  Destination:{' '}
                  {destinations.find((d) => d.id.toString() === selectedDestination)?.name}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSelectedDestination('')}
                  />
                </span>
              )}
              {selectedPackageType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm">
                  Type: {packageTypes.find((t) => t.id.toString() === selectedPackageType)?.name}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSelectedPackageType('')}
                  />
                </span>
              )}
              {(dateFrom || dateTo) && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm">
                  Dates: {dateFrom} - {dateTo}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => {
                      setDateFrom('')
                      setDateTo('')
                    }}
                  />
                </span>
              )}
              {priceRange && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm">
                  Price:{' '}
                  {priceRange === '0'
                    ? 'Under R5000'
                    : priceRange === '5-10'
                      ? 'R5000 - R10000'
                      : priceRange === '10-20'
                        ? 'R10000 - R20000'
                        : priceRange === '20'
                          ? 'R20000 Upwards'
                          : ''}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setPriceRange('')} />
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Results Info */}
        <div className="mx-auto mt-8 text-center text-sm text-gray-600">
          Showing {packages.length} of {pagination.total_count} packages
          {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
          {searchTerm && ` matching "${searchTerm}"`}
        </div>

        {/* Packages Grid */}
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {packages.map((pkg) => {
            const imageUrl = getImageUrl(pkg)

            return (
              <article key={pkg.id} className="flex flex-col items-start group">
                <Link to={`/packages/${pkg.slug}`} className="w-full">
                  <div className="relative w-full">
                    {/* Image with franchise accent color background */}
                    <div
                      className="aspect-[16/9] w-full rounded-2xl object-cover sm:aspect-[2/1] lg:aspect-[3/2] flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300"
                      style={{
                        backgroundColor: franchiseData?.accent_color || '#F896C7',
                        backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      {!imageUrl && <Plane className="h-12 w-12 text-white opacity-60" />}
                    </div>
                    <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-full text-sm font-medium">
                      {pkg.category}
                    </div>
                    <div className="absolute top-4 right-4 bg-[#182c78] text-white px-3 py-1 rounded-full text-sm font-medium">
                      {pkg.nights} {pkg.nights === 1 ? 'Night' : 'Nights'}
                    </div>
                  </div>
                </Link>

                <div className="max-w-xl w-full">
                  <div className="mt-8 flex items-center gap-x-4 text-xs">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: pkg.rating || 5 }).map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <span className="text-gray-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {pkg.duration}
                    </span>
                  </div>
                  <div className="group relative">
                    <Link to={`/packages/${pkg.slug}`}>
                      <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600 transition-colors">
                        {pkg.name}
                      </h3>
                    </Link>
                    <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
                      {pkg.intro || pkg.description}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {pkg.includes?.map((item) => (
                      <span
                        key={item}
                        className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                        style={{
                          backgroundColor: `#182c7820`,
                          color: '#182c78',
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-x-4">
                    <div className="flex items-center gap-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{pkg.dates}</span>
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-2xl font-bold" style={{ color: '#182c78' }}>
                        From {pkg.formatted_price} pps
                      </span>
                      <span className="text-xs text-gray-500">{pkg.total_pax}</span>
                    </div>
                    <Link to={`/packages/${pkg.slug}`} className="btn-primary">
                      View Details
                    </Link>
                  </div>
                </div>
              </article>
            )
          })}
        </div>

        {/* No results */}
        {packages.length === 0 && !loading && (
          <div className="mx-auto mt-16 max-w-2xl text-center">
            <h3 className="text-lg font-semibold text-gray-900">No packages found</h3>
            <p className="mt-2 text-sm text-gray-600">
              Try adjusting your search or filter criteria.
            </p>
            <button onClick={clearAllFilters} className="mt-4 btn-secondary">
              Clear all filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mx-auto mt-20 flex items-center justify-center gap-4">
            <button
              onClick={goToPrevPageWithUrl}
              disabled={!pagination.has_prev}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
                let page
                if (pagination.total_pages <= 7) {
                  page = i + 1
                } else {
                  const current = pagination.current_page
                  if (current <= 4) {
                    page = i + 1
                  } else if (current >= pagination.total_pages - 3) {
                    page = pagination.total_pages - 6 + i
                  } else {
                    page = current - 3 + i
                  }
                }

                return (
                  <button
                    key={page}
                    onClick={() => loadPageWithUrl(page)}
                    className={`mx-1 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      page === pagination.current_page
                        ? 'bg-[#182c78] text-white'
                        : 'bg-white text-gray-700 hover:bg-[#182c78] hover:text-white border border-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
            </div>

            <button
              onClick={goToNextPageWithUrl}
              disabled={!pagination.has_next}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {loading && packages.length > 0 && (
          <div className="mx-auto mt-20 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600">
              <div className="animate-spin h-4 w-4 border-2 border-chelsea-orange border-t-transparent rounded-full mr-2"></div>
              Loading...
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-24 rounded-2xl bg-gray-50 px-6 py-16 sm:px-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              Can't find what you're looking for?
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Our travel experts can create a custom package just for you. Get in touch and let us
              plan your perfect getaway.
            </p>
            <div className="mt-8">
              <a href="/contact" className="btn-primary">
                Contact Our Experts
              </a>
            </div>
          </div>
        </div>

        {/* See all packages link */}
        <div className="mt-24 rounded-2xl bg-gray-50 px-6 py-16 sm:px-16">
          <div className="mx-auto max-w-2xl text-center">
            <a
              href="/packages"
              className="text-sm font-semibold leading-6"
              style={{ color: '#182c78' }}
            >
              See all packages <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
